import { VITAL_CODES } from './vital-codes';
import { NOT_MENSTRUATING_VALUE, parseLmpDateValue } from './lmp-editor';
import { parseVisualAcuityValue } from '@scripsteam/scrips-signal-ds';
import {
  ALLOWED_DENOMINATOR as ALLOWED_DENOMINATORS,
  ALLOWED_NUMERATOR as ALLOWED_NUMERATORS,
} from '@/encounter/lib/visual-acuity';

/**
 * Vitals form validation — mirrors Flutter
 * `vital_utils.dart:isValidVitalsData()` (lines 25-105).
 *
 * Rules ported from Flutter (verbatim — these are the strings the
 * backend's e-Claim integration relies on for user-facing error
 * surfaces):
 *
 *   1. **At least one value**: every save must have ≥1 non-empty
 *      quantity. Empty save → "Vitals are empty, please add at least
 *      one vital".
 *
 *   2. **BP both-or-neither**: if systolic OR diastolic is set, BOTH
 *      must be set. "Please add both values for BP".
 *
 *   3. **Hip circumference range**: 30 ≤ value ≤ 200 cm.
 *      "Please enter Hip Circumference value between 30 and 200".
 *
 *   4. **Waist circumference range**: same — 30 ≤ value ≤ 200 cm.
 *
 *   5. **LMP not in the future** (PR 3a hardening — Samer review §3).
 *      Calendar picker disables future dates in the UI, but a
 *      determined user can paste / devtools a future string. This is
 *      the server-bound guard so submitting a future LMP fails
 *      cleanly with a user-facing message rather than a 400 from the
 *      .NET binder. Sentinel ("Not menstruating") explicitly skips.
 *
 *   6. **Visual Acuity** (PR 3b — shipped):
 *      - OS / OD each both-or-neither (numerator + denominator)
 *      - Numerator must be `"20"` (`allowedNumeratorValues`)
 *      - Denominator must be in `allowedValues` (10/15/20/.../400)
 *      Per Flutter `vital_utils.dart:76-102`.
 *
 *   7. Mandatory vitals (e.g. weight when eClaim enabled) — DEFERRED
 *      to PR 2.5. Flutter's `mandatoryVitals()` returns `[weight]`
 *      when `isEClaimEnabled` is true, otherwise `[]`. We'll need the
 *      eClaim status query (`useEClaimStatus`) wired before enforcing.
 *
 * Returns the first error message found, or null if valid.
 *
 * `values` is a record keyed by LOINC code with trimmed string values
 * (so callers don't have to thread `quantity.value` through). Empty
 * strings count as "not set"; never undefined.
 */

export function validateVitalsForm(values: Record<string, string>): string | null {
  // Rule 1 — at least one non-empty value.
  const hasAnyValue = Object.values(values).some((v) => v.trim() !== '');
  if (!hasAnyValue) {
    return 'Vitals are empty, please add at least one vital';
  }

  // Rule 2 — BP both-or-neither.
  const systolic = (values[VITAL_CODES.SYSTOLIC] ?? '').trim();
  const diastolic = (values[VITAL_CODES.DIASTOLIC] ?? '').trim();
  if ((systolic !== '' && diastolic === '') || (systolic === '' && diastolic !== '')) {
    return 'Please add both values for BP';
  }

  // Rule 3 — Hip circumference 30..200.
  const hip = (values[VITAL_CODES.HIP] ?? '').trim();
  if (hip !== '') {
    const n = Number(hip);
    if (Number.isFinite(n) && (n < 30 || n > 200)) {
      return 'Please enter Hip Circumference value between 30 and 200';
    }
  }

  // Rule 4 — Waist circumference 30..200.
  const waist = (values[VITAL_CODES.WAIST] ?? '').trim();
  if (waist !== '') {
    const n = Number(waist);
    if (Number.isFinite(n) && (n < 30 || n > 200)) {
      return 'Please enter Waist Circumference value between 30 and 200';
    }
  }

  // Rule 5 — LMP cannot be a future date. Samer review PR #154 §3.
  const lmp = (values[VITAL_CODES.LMP] ?? '').trim();
  if (lmp !== '' && lmp !== NOT_MENSTRUATING_VALUE) {
    const parsed = parseLmpDateValue(lmp);
    if (parsed) {
      // Compare to start-of-tomorrow so today's date is valid even at
      // the edge of midnight in the user's timezone.
      const startOfTomorrow = new Date();
      startOfTomorrow.setHours(0, 0, 0, 0);
      startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
      if (parsed >= startOfTomorrow) {
        return 'LMP date cannot be in the future';
      }
    }
  }

  // Rule 6 — Visual Acuity (OS / OD both-or-neither + allowed values).
  const visual = (values[VITAL_CODES.VISUAL] ?? '').trim();
  if (visual !== '') {
    const { osNum, osDen, odNum, odDen } = parseVisualAcuityValue(visual);
    // OS: both-or-neither
    const osNumEmpty = osNum === '';
    const osDenEmpty = osDen === '';
    if (osNumEmpty !== osDenEmpty) {
      return 'Please add both values for OS Visual Acuity';
    }
    if (!osNumEmpty && !osDenEmpty) {
      if (
        !(ALLOWED_NUMERATORS as readonly string[]).includes(osNum) ||
        !(ALLOWED_DENOMINATORS as readonly string[]).includes(osDen)
      ) {
        return 'Please enter valid values for both OS Visual Acuity';
      }
    }
    // OD: both-or-neither
    const odNumEmpty = odNum === '';
    const odDenEmpty = odDen === '';
    if (odNumEmpty !== odDenEmpty) {
      return 'Please add both values for OD Visual Acuity';
    }
    if (!odNumEmpty && !odDenEmpty) {
      if (
        !(ALLOWED_NUMERATORS as readonly string[]).includes(odNum) ||
        !(ALLOWED_DENOMINATORS as readonly string[]).includes(odDen)
      ) {
        return 'Please enter valid values for both OD Visual Acuity';
      }
    }
  }

  return null;
}
